import Modal from '@betterfunnel/ui-core/Modal';
import Select from '@betterfunnel/ui-core/Select';
import ToggleButton from '@betterfunnel/ui-core/ToggleButton';
import { Domain } from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useContext, useMemo, useState } from 'react';
import CopyTextField from '../components/CopyTextField';
import { useDashboardContext } from '../contexts/DashboardContext';
import { DynamicPageContext } from '../contexts/DynamicPageContext';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CopyDynamicPageLinkModal({ open, onClose }: Props) {
  const { selectedDynamicPage } = useDashboardContext();
  const { relevantDomains, primaryDomain } = useContext(DynamicPageContext);

  const [selectedDomain, setSelectedDomain] =
    useState<QueryDocumentSnapshot<Domain>>();

  const [integration, setIntergration] = useState<
    'google-ads' | 'facebook-ads' | 'bing-ads'
  >('google-ads');

  const link = useMemo(
    () =>
      `https://${(selectedDomain ?? primaryDomain)?.data().domain}/${
        selectedDynamicPage?.data().path
      }`,
    [selectedDomain, primaryDomain, selectedDynamicPage],
  );

  const finalUrl = useMemo(() => {
    switch (integration) {
      case 'google-ads':
        return `${link}?keyword={keyword}&gclid={gclid}&adid={creative}&adgroupid={adgroupid}&device={device}`;
      case 'facebook-ads':
        return link;
      case 'bing-ads':
        return `${link}?keyword={keyword}&msclkid={msclkid}`;
    }
  }, [integration, link]);

  return (
    <Modal open={open} onClose={onClose} width={'lg'}>
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Copy Dynamic Page Link
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-3 pb-6">
        {(relevantDomains?.length ?? 0) > 1 && (
          <Select<string | number>
            title="Domain"
            selected={
              selectedDomain?.id ||
              relevantDomains?.find((rd) => rd.id === primaryDomain?.id)?.id ||
              relevantDomains?.[0]?.id
            }
            options={
              relevantDomains?.map((d) => ({
                value: d.id,
                child: d.data().domain,
              })) || []
            }
            onChange={(selected) => {
              setSelectedDomain(
                relevantDomains?.find((d) => d.id === selected),
              );
            }}
          />
        )}
        <CopyTextField title="Dynamic page link" text={link} />
        <div className="mt-4 whitespace-nowrap">
          <ToggleButton<'google-ads' | 'facebook-ads' | 'bing-ads'>
            value={integration}
            items={[
              { value: 'google-ads', title: 'Google Ads' },
              { value: 'facebook-ads', title: 'Facebook Ads' },
              { value: 'bing-ads', title: 'Bing Ads' },
            ]}
            onChange={(selected) => setIntergration(selected)}
          />
        </div>
        <CopyTextField text={finalUrl} />
      </div>
    </Modal>
  );
}
